/** 手机触控：独立操作按钮使用至少44px点击区域；桌面保持原紧凑布局，业务权限、提交与跳转逻辑不变。 */
import React, { useState } from "react";
import { toast } from "sonner";
import { X, Plus, Trash2, ShieldCheck, Sparkles, Loader2 } from "lucide-react";

import { T } from "../../lib/i18n";
import { useAiModels } from "../../lib/aiModels";

const SEVERITIES = ["Critical", "Major", "Minor"];
const SEV_COLOR = { Critical: "bg-red-100 text-red-700", Major: "bg-amber-100 text-amber-700", Minor: "bg-slate-100 text-slate-600" };
const DEFECT_TYPES = ["尺寸/标注", "规范合规", "图层/命名", "结构/受力", "出图完整性", "业主需求偏差", "其它"];

export default function QAModal({ task, api, zh, lang, onClose, onChanged }) {
  const aiModels = useAiModels();
  const [result, setResult] = useState("pass");
  const [defects, setDefects] = useState([]);
  const [saving, setSaving] = useState(false);
  const [precheck, setPrecheck] = useState(null);
  const [loadingAI, setLoadingAI] = useState(false);

  const addDefect = () => setDefects((d) => [...d, { severity: "Minor", type: DEFECT_TYPES[0], note: "" }]);
  const upd = (i, k, v) => setDefects((d) => d.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const rm = (i) => setDefects((d) => d.filter((_, j) => j !== i));

  const runPrecheck = async () => {
    setLoadingAI(true);
    try {
      const { data } = await api.post(`/design/tasks/${task.id}/ai/qa-precheck?lang=${lang || "zh"}`);
      setPrecheck(data.items || []);
    } catch (e) { toast.error(e?.detailText || "failed"); } finally { setLoadingAI(false); }
  };
  const addFromAI = (it) => {
    if (result === "pass") setResult("rework");
    setDefects((d) => [...d, { severity: it.severity || "Minor", type: DEFECT_TYPES.includes(it.type) ? it.type : /* raw */ "其它", note: it.note || "" }]);
  };

  const submit = async () => {
    if (result === "rework" && defects.length === 0) return toast.error(zh ? "打回需至少记录一条缺陷" : "Rework needs a defect");
    setSaving(true);
    try {
      await api.post(`/design/tasks/${task.id}/qa`, { result, defects });
      toast.success(result === "pass" ? (zh ? "QA 通过" : "QA passed") : (zh ? "已打回返工" : "Sent to rework"));
      onChanged && (await onChanged());
      onClose();
    } catch (e) { toast.error(e?.detailText || "failed"); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="design-qa-modal">
      <div className="bg-white w-full max-w-xl overflow-y-auto rounded-2xl max-h-[90dvh]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 flex-wrap md:flex-nowrap">
          <p className="text-sm font-bold text-slate-800 flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-indigo-600" />{zh ? "质量评审 (QA)" : "QA Review"} · {T(task.title)}</p>
          <button onClick={onClose} data-testid="design-qa-close" className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="flex gap-2">
            {[["pass", zh ? "通过" : "Pass"], ["rework", zh ? "打回返工" : "Rework"]].map(([k, l]) => (
              <button key={k} onClick={() => setResult(k)} data-testid={`design-qa-result-${k}`}
                className={"min-h-11 min-w-11 md:min-h-0 md:min-w-0 " + (`flex-1 py-2 rounded-lg text-sm font-medium border ${result === k ? (k === "pass" ? "bg-emerald-600 text-white border-emerald-600" : "bg-red-600 text-white border-red-600") : "bg-white text-slate-600 border-slate-200"}`)}>{l}</button>
            ))}
          </div>

          <div className="rounded-lg border border-indigo-100 bg-indigo-50/50 p-3">
            <button onClick={runPrecheck} disabled={loadingAI} data-testid="design-qa-ai-precheck" className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-[12px] text-indigo-700 font-medium flex items-center gap-1.5 disabled:opacity-50">
              {loadingAI ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}{zh ? `AI 质量预检（${aiModels.extract || "AI"} 生成建议检查点）` : "AI QA pre-check"}
            </button>
            {precheck && (
              <div className="mt-2 space-y-1" data-testid="design-qa-ai-suggestions">
                {precheck.length === 0 ? <p className="text-[11px] text-slate-400">{zh ? "无建议" : "None"}</p> : precheck.map((it, i) => (
                  <button key={i} onClick={() => addFromAI(it)} data-testid={`design-qa-ai-suggestion-${i}`} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 w-full text-left flex items-start gap-1.5 text-[11px] text-slate-600 hover:bg-white rounded px-1.5 py-1">
                    <Plus className="h-3 w-3 mt-0.5 text-indigo-500 shrink-0" /><span className={`px-1 rounded ${SEV_COLOR[it.severity] || SEV_COLOR.Minor}`}>{it.severity}</span><span>{T(it.note)}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <div className="flex-wrap md:flex-nowrap flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-slate-600">{zh ? "缺陷清单" : "Defects"}</p>
              <button onClick={addDefect} data-testid="design-qa-add-defect" className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-[11px] text-indigo-600 flex items-center gap-1"><Plus className="h-3 w-3" />{zh ? "添加缺陷" : "Add"}</button>
            </div>
            {defects.length === 0 && <p className="text-[11px] text-slate-400">{zh ? "无缺陷（Pass 可不填）" : "No defects"}</p>}
            <div className="space-y-2">
              {defects.map((d, i) => (
                <div key={i} className="flex-wrap md:flex-nowrap flex items-center gap-1.5" data-testid={`design-qa-defect-${i}`}>
                  <select value={d.severity} onChange={(e) => upd(i, "severity", e.target.value)} className={`text-[11px] rounded px-1.5 py-1 border-0 ${SEV_COLOR[d.severity]}`} data-testid={`design-qa-defect-severity-${i}`}>
                    {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <select value={d.type} onChange={(e) => upd(i, "type", e.target.value)} className="text-[11px] rounded border border-slate-200 px-1.5 py-1 bg-white">
                    {DEFECT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <input value={d.note} onChange={(e) => upd(i, "note", e.target.value)} placeholder={zh ? "说明" : "note"} className="flex-1 text-[11px] rounded border border-slate-200 px-2 py-1" data-testid={`design-qa-defect-note-${i}`} />
                  <button onClick={() => rm(i)} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-slate-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              ))}
            </div>
          </div>
          <p className="text-[10px] text-slate-400">{zh ? "缺陷权重：Critical 15 / Major 6 / Minor 2，计入质量分（观察期只统计不关联奖惩）。打回将 +1 返工并置为 Correction Required。" : "Weights Critical15/Major6/Minor2. Observation only."}</p>
          <div className="flex-wrap md:flex-nowrap flex justify-end gap-2">
            <button onClick={onClose} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 px-3 py-1.5 text-sm text-slate-500">{zh ? "取消" : "Cancel"}</button>
            <button onClick={submit} disabled={saving} data-testid="design-qa-submit" className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 px-4 py-1.5 text-sm rounded-lg bg-indigo-600 text-white disabled:opacity-50">{zh ? "提交评审" : "Submit"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
